import { Component, ViewChild, AfterViewInit, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DashboardMetricsService, DashboardMetrics, MetricData, DateFilter, Store, ConsumptionTrendData, TopConsumedProductsData, TopStoresByActivityData, MostConsumedProductTypeData, ExpiredSummaryTableData, AboutToExpiryTableData } from '../services/dashboard-metrics.service';
import { DashboardFiltersComponent } from './filters/dashboard-filters/dashboard-filters.component';
import { DateRangeContainerComponent } from './date-range/date-range-container/date-range-container.component';
import { DashboardMetricsComponent } from './metrics/dashboard-metrics/dashboard-metrics.component';
import { ConsumptionTrendChartComponent } from './charts/consumption-trend-chart/consumption-trend-chart.component';
import { TopConsumedProductsChartComponent } from './charts/top-consumed-products-chart/top-consumed-products-chart.component';
import { TopStoresActivityChartComponent } from './charts/top-stores-activity-chart/top-stores-activity-chart.component';
import { MostConsumedProductTypeChartComponent } from './charts/most-consumed-product-type-chart/most-consumed-product-type-chart.component';
import { ExpiredProductSummaryChartComponent } from './charts/expired-product-summary-chart/expired-product-summary-chart.component';
import { ExpiringSoonChartComponent } from './charts/expiring-soon-chart/expiring-soon-chart.component';
import { LowStockChartComponent } from './charts/low-stock-chart/low-stock-chart.component';
import { WarehouseV2DashboardComponent } from './warehouse/warehouse-v2-dashboard.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-dashboard-v2',
  standalone: true,
  imports: [CommonModule, FormsModule, DashboardFiltersComponent, DashboardMetricsComponent, ConsumptionTrendChartComponent, TopConsumedProductsChartComponent, MostConsumedProductTypeChartComponent, ExpiredProductSummaryChartComponent, ExpiringSoonChartComponent, LowStockChartComponent],
  templateUrl: './dashboard-v2.component.html',
  styleUrl: './dashboard-v2.component.scss'
})
export class DashboardV2Component implements OnInit, AfterViewInit, OnDestroy {
  // Filters
  dateFilter: string = 'today'; // Default to today
  customFromDate: string = '';
  customToDate: string = '';
  storeFilter: string = ''; // Will be set after loading stores

  // Loading states
  isLoading: boolean = false;
  isLoadingFilters: boolean = false;
  isLoadingStores: boolean = false;
  isLoadingConsumptionTrend: boolean = false;
  isLoadingTopConsumedProducts: boolean = false;
  isLoadingTopStoresByActivity: boolean = false;
  isLoadingMostConsumedProductType: boolean = false;
  isLoadingExpiredSummary: boolean = false;
  isLoadingAboutToExpiry: boolean = false;
  isLoadingLowStock: boolean = false;
  lowStockData: any[] = [];

  // Data
  dateFilters: DateFilter[] = [];
  stores: Store[] = [];
  metricsData: DashboardMetrics = {};
  consumptionTrendData: ConsumptionTrendData | null = null;
  topConsumedProductsData: TopConsumedProductsData | null = null;
  topStoresByActivityData: TopStoresByActivityData | null = null;
  mostConsumedProductTypeData: MostConsumedProductTypeData | null = null;
  expiredSummaryData: ExpiredSummaryTableData[] = [];
  aboutToExpiryData: AboutToExpiryTableData[] = [];
  
  // Date range display
  resolvedDateRange: string = '';

  // Accent colors for metric boxes - WCAG 2.1 AA compliant
  accentColors: string[] = [
    '#2563eb', // Active - Blue (high contrast)
    '#059669', // Consumed - Green (high contrast)
    '#be185d', // Missing - Pink (high contrast)
    '#dc2626', // Expired - Red (high contrast)
    '#ea580c', // Expiring in 30 Days - Orange (high contrast)
    '#7c3aed', // Low Stock - Purple (high contrast)
    '#0891b2', // Transit - Cyan (high contrast)
    '#dc2626', // Devices - Red (high contrast)

  ];

  // Metric summary boxes (will be populated from API)
  metrics = [
    { label: 'Active', count: 0, percent: '0.00', trend: 'up', key: 'active_stocks' },
    { label: 'Consumed', count: 0, percent: '0.00', trend: 'down', key: 'consumed_stocks' },
    { label: 'Missing', count: 0, percent: '0.00', trend: 'down', key: 'missing_stocks' },
    { label: 'Expired', count: 0, percent: '0.00', trend: 'up', key: 'expired_stocks' },
    { label: 'Expiring in 30 Days', count: 0, percent: '0.00', trend: 'up', key: 'expiring_stocks' },
    { label: 'Low Stock', count: 0, percent: '0.00', trend: 'down', key: 'low_stocks' },
    { label: 'Transit', count: 0, percent: '0.00', trend: 'up', key: 'transit_stocks' },
    { label: 'Devices', count: 0, percent: '0.00', trend: 'stable', key: 'devices' },

  ];

  // Top Stores Activity Toggle
  topStoresActivityType: 'consumed' | 'missing' = 'consumed';

  // Most Consumed Product Type Toggle
  mostConsumedProductTypeCategory: 'body_part' | 'implant' = 'implant';



  constructor(
    private dashboardMetricsService: DashboardMetricsService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit() {
    // Restore dashboard state if available
    const savedState = sessionStorage.getItem('dashboardV2State');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        this.dateFilter = state.dateFilter || this.dateFilter;
        this.customFromDate = state.customFromDate || this.customFromDate;
        this.customToDate = state.customToDate || this.customToDate;
        this.storeFilter = state.storeFilter || this.storeFilter;
      } catch (e) { /* ignore */ }
      // Optionally clear the saved state after restoring
      // sessionStorage.removeItem('dashboardV2State');
    }
    console.log('Dashboard V2 Component initialized');
    console.log('Initial dateFilter:', this.dateFilter);
    console.log('Initial storeFilter:', this.storeFilter);
    
    this.calculateDateRange();
    this.loadDateFilters();
    this.loadUserStores();
  }

  ngAfterViewInit() {
    // Charts are now handled by child components
    console.log('Dashboard V2 Component view initialized');
  }

  ngOnDestroy() {
    console.log('Dashboard V2 Component destroyed');
  }

  /**
   * Load available date filters
   */
  loadDateFilters() {
    this.isLoadingFilters = true;
    this.dashboardMetricsService.getDateFilters().subscribe({
      next: (response) => {
        if (response.success) {
          this.dateFilters = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading date filters:', error);
      },
      complete: () => {
        this.isLoadingFilters = false;
      }
    });
  }

  /**
   * Load user stores
   */
  loadUserStores() {
    this.isLoadingStores = true;
    this.dashboardMetricsService.getUserStores().subscribe({
      next: (response) => {
        if (response.success && response.data.length > 0) {
          this.stores = response.data;
          
          // Validate and set store filter
          this.validateAndSetStoreFilter();
          
          // Load metrics after stores are loaded
          this.loadMetrics();
        }
      },
      error: (error) => {
        console.error('Error loading user stores:', error);
      },
      complete: () => {
        this.isLoadingStores = false;
      }
    });
  }

  /**
   * Validate and set store filter
   */
  private validateAndSetStoreFilter() {
    console.log('Validating store filter. Current selection:', this.storeFilter);
    console.log('Available stores:', this.stores.map(s => ({id: s.id, name: s.name})));
    
    // Check for default store ID from login response
    const defaultStoreId = localStorage.getItem('defaultStoreId');
    
    if (defaultStoreId) {
      // Check if the default store exists in the available stores
      const defaultStoreExists = this.stores.some(store => store.id.toString() === defaultStoreId);
      
      if (defaultStoreExists) {
        this.storeFilter = defaultStoreId;
        console.log('Default store found, setting to default store:', this.storeFilter);
        // Clear the default store ID from localStorage after using it
        localStorage.removeItem('defaultStoreId');
        return;
      } else {
        console.log('Default store not found in available stores, will select first store');
        localStorage.removeItem('defaultStoreId');
      }
    }
    
    // If no store is selected or default store not found, select the first one
    if (!this.storeFilter) {
      this.storeFilter = this.stores[0].id.toString();
      console.log('No store selected, setting to first store:', this.storeFilter);
      return;
    }

    // Check if the currently selected store exists in the new store list
    const selectedStoreExists = this.stores.some(store => store.id.toString() === this.storeFilter);
    
    if (!selectedStoreExists) {
      this.storeFilter = this.stores[0].id.toString();
      console.log('Selected store not found, setting to first store:', this.storeFilter);
    }
  }

  /**
   * Load dashboard metrics
   */
  loadMetrics() {
    // Validate custom date range before making API call
    if (this.dateFilter === 'custom' && (!this.customFromDate || !this.customToDate)) {
      console.warn('Custom date range is incomplete. Skipping metrics load.');
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    
    const params: any = {
      date_filter: this.dateFilter,
      store_id: parseInt(this.storeFilter),
      counts: 'active,consumed,missing,expired,expiring,low_stocks,transit,devices'
    };

    if (this.dateFilter === 'custom') {
      params.custom_from_date = this.customFromDate;
      params.custom_to_date = this.customToDate;
    }

    console.log('Loading metrics with params:', params);

    this.dashboardMetricsService.getMetrics(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.metricsData = response.data;
          // Use API date_range for display if available
          if (response.date_range && response.date_range.current_start && response.date_range.current_end) {
            this.resolvedDateRange = this.formatDateRange(response.date_range.current_start, response.date_range.current_end);
            this.cdr.detectChanges();
          } else if (this.dateFilter === 'custom') {
            // Fallback to manual calculation for custom
            this.calculateDateRange();
          }
          this.updateMetricsDisplay();
          
          // Load chart data after metrics are loaded
          this.loadAllChartData();
        }
      },
      error: (error) => {
        console.error('Error loading metrics:', error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  private formatDateRange(start: string, end: string): string {
    const format = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };
    return `${format(start)} - ${format(end)}`;
  }

  /**
   * Update metrics display
   */
  updateMetricsDisplay() {
    this.metrics.forEach(metric => {
      const data = this.metricsData[metric.key as keyof DashboardMetrics] as MetricData;
      if (data) {
        metric.count = data.count || 0;
        metric.percent = (data.percentage_change || 0).toString();
        metric.trend = data.trend || 'stable';
        
        // Ensure percentage is formatted as string with 2 decimal places
        if (typeof data.percentage_change === 'number') {
          metric.percent = data.percentage_change.toFixed(2);
        } else if (typeof data.percentage_change === 'string') {
          metric.percent = data.percentage_change;
        } else {
          metric.percent = '0.00';
        }
      }
    });
  }

  /**
   * Load all chart data
   */
  loadAllChartData() {
    this.loadConsumptionTrend();
    this.loadTopConsumedProducts();
    this.loadMostConsumedProductType();
    this.loadLowStock();
    this.loadExpiredSummary();
    this.loadAboutToExpiry();
  }

  /**
   * Load consumption trend data
   */
  loadConsumptionTrend() {
    // Validate custom date range before making API call
    if (this.dateFilter === 'custom' && (!this.customFromDate || !this.customToDate)) {
      console.warn('Custom date range is incomplete. Skipping consumption trend load.');
      this.isLoadingConsumptionTrend = false;
      return;
    }

    this.isLoadingConsumptionTrend = true;
    
    const params: any = {
      date_filter: this.dateFilter,
      store_id: parseInt(this.storeFilter)
    };
    if (this.dateFilter === 'custom') {
      params.custom_from_date = this.customFromDate;
      params.custom_to_date = this.customToDate;
    }

    this.dashboardMetricsService.getConsumptionTrend(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.consumptionTrendData = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading consumption trend:', error);
      },
      complete: () => {
        this.isLoadingConsumptionTrend = false;
      }
    });
  }

  /**
   * Load top consumed products data
   */
  loadTopConsumedProducts() {
    // Validate custom date range before making API call
    if (this.dateFilter === 'custom' && (!this.customFromDate || !this.customToDate)) {
      console.warn('Custom date range is incomplete. Skipping top consumed products load.');
      this.isLoadingTopConsumedProducts = false;
      return;
    }

    this.isLoadingTopConsumedProducts = true;
    
    const params: any = {
      date_filter: this.dateFilter,
      store_id: parseInt(this.storeFilter)
    };
    if (this.dateFilter === 'custom') {
      params.custom_from_date = this.customFromDate;
      params.custom_to_date = this.customToDate;
    }

    this.dashboardMetricsService.getTopConsumedProducts(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.topConsumedProductsData = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading top consumed products:', error);
      },
      complete: () => {
        this.isLoadingTopConsumedProducts = false;
      }
    });
  }

  /**
   * Load top stores by activity data
   */
  loadTopStoresByActivity() {
    // Validate custom date range before making API call
    if (this.dateFilter === 'custom' && (!this.customFromDate || !this.customToDate)) {
      console.warn('Custom date range is incomplete. Skipping top stores by activity load.');
      this.isLoadingTopStoresByActivity = false;
      return;
    }

    this.isLoadingTopStoresByActivity = true;
    
    const params: any = {
      date_filter: this.dateFilter,
      store_id: parseInt(this.storeFilter),
      activity_type: this.topStoresActivityType
    };
    if (this.dateFilter === 'custom') {
      params.custom_from_date = this.customFromDate;
      params.custom_to_date = this.customToDate;
    }

    this.dashboardMetricsService.getTopStoresByActivity(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.topStoresByActivityData = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading top stores by activity:', error);
      },
      complete: () => {
        this.isLoadingTopStoresByActivity = false;
      }
    });
  }

  /**
   * Load most consumed product type data
   */
  loadMostConsumedProductType() {
    // Validate custom date range before making API call
    if (this.dateFilter === 'custom' && (!this.customFromDate || !this.customToDate)) {
      console.warn('Custom date range is incomplete. Skipping most consumed product type load.');
      this.isLoadingMostConsumedProductType = false;
      return;
    }

    this.isLoadingMostConsumedProductType = true;
    
    const params: any = {
      date_filter: this.dateFilter,
      store_id: parseInt(this.storeFilter),
      category_type: this.mostConsumedProductTypeCategory
    };
    if (this.dateFilter === 'custom') {
      params.custom_from_date = this.customFromDate;
      params.custom_to_date = this.customToDate;
    }

    this.dashboardMetricsService.getMostConsumedProductType(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.mostConsumedProductTypeData = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading most consumed product type:', error);
      },
      complete: () => {
        this.isLoadingMostConsumedProductType = false;
      }
    });
  }

  /**
   * Load expired summary data
   */
  loadExpiredSummary() {
    // Validate custom date range before making API call
    if (this.dateFilter === 'custom' && (!this.customFromDate || !this.customToDate)) {
      console.warn('Custom date range is incomplete. Skipping expired summary load.');
      this.isLoadingExpiredSummary = false;
      return;
    }

    this.isLoadingExpiredSummary = true;
    
    const params: any = {
      date_filter: this.dateFilter,
      store_id: parseInt(this.storeFilter)
    };
    if (this.dateFilter === 'custom') {
      params.custom_from_date = this.customFromDate;
      params.custom_to_date = this.customToDate;
    }

    this.dashboardMetricsService.getExpiredSummary(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.expiredSummaryData = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading expired summary:', error);
      },
      complete: () => {
        this.isLoadingExpiredSummary = false;
      }
    });
  }

  /**
   * Load about to expiry data
   */
  loadAboutToExpiry() {
    // Validate custom date range before making API call
    if (this.dateFilter === 'custom' && (!this.customFromDate || !this.customToDate)) {
      console.warn('Custom date range is incomplete. Skipping about to expiry load.');
      this.isLoadingAboutToExpiry = false;
      return;
    }

    this.isLoadingAboutToExpiry = true;
    
    const params: any = {
      date_filter: this.dateFilter,
      store_id: parseInt(this.storeFilter)
    };
    if (this.dateFilter === 'custom') {
      params.custom_from_date = this.customFromDate;
      params.custom_to_date = this.customToDate;
    }

    this.dashboardMetricsService.getAboutToExpiry(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.aboutToExpiryData = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading about to expiry:', error);
      },
      complete: () => {
        this.isLoadingAboutToExpiry = false;
      }
    });
  }

  /**
   * Load low stock data
   */
  loadLowStock() {
    if (this.dateFilter === 'custom' && (!this.customFromDate || !this.customToDate)) {
      console.warn('Custom date range is incomplete. Skipping low stock load.');
      this.isLoadingLowStock = false;
      return;
    }
    this.isLoadingLowStock = true;
    const params: any = {
      date_filter: this.dateFilter,
      store_id: parseInt(this.storeFilter)
    };
    if (this.dateFilter === 'custom') {
      params.custom_from_date = this.customFromDate;
      params.custom_to_date = this.customToDate;
    }
    this.dashboardMetricsService.getLowStock(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.lowStockData = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading low stock:', error);
      },
      complete: () => {
        this.isLoadingLowStock = false;
      }
    });
  }

  /**
   * Handle custom search
   */
  onCustomSearch() {
    console.log('Custom search triggered:', {
      dateFilter: this.dateFilter,
      customFromDate: this.customFromDate,
      customToDate: this.customToDate
    });
    this.calculateDateRange();
    this.loadMetrics();
  }

  /**
   * Handle custom from date change
   */
  onCustomFromDateChange(date: string) {
    this.customFromDate = date;
    this.checkAndLoadCustomMetrics();
  }

  /**
   * Handle custom to date change
   */
  onCustomToDateChange(date: string) {
    this.customToDate = date;
    this.checkAndLoadCustomMetrics();
  }

  /**
   * Check if both custom dates are selected and load metrics if so
   */
  private checkAndLoadCustomMetrics() {
    if (this.dateFilter === 'custom' && this.customFromDate && this.customToDate) {
      console.log('Both custom dates selected, loading metrics');
      this.calculateDateRange();
      this.loadMetrics();
    }
  }

  /**
   * Check if custom date range is incomplete
   */
  get isCustomDateRangeIncomplete(): boolean {
    return this.dateFilter === 'custom' && (!this.customFromDate || !this.customToDate);
  }

  /**
   * Handle date filter change
   */
  onDateFilterChange() {
    // If a predefined filter is selected, clear custom dates
    if (this.dateFilter !== 'custom') {
      this.customFromDate = '';
      this.customToDate = '';
    }
    console.log('Date filter changed to:', {
      dateFilter: this.dateFilter,
      customFromDate: this.customFromDate,
      customToDate: this.customToDate
    });
    this.calculateDateRange();
    
    // Only load metrics if not switching to custom filter (which requires both dates)
    if (this.dateFilter !== 'custom') {
      this.loadMetrics();
    }
  }

  /**
   * Handle store filter change
   */
  onStoreFilterChange() {
    console.log('Store filter changed to:', this.storeFilter);
    this.loadMetrics();
  }

  /**
   * Toggle top stores activity type
   */
  toggleTopStoresActivity() {
    this.topStoresActivityType = this.topStoresActivityType === 'consumed' ? 'missing' : 'consumed';
    this.loadTopStoresByActivity();
  }

  /**
   * Toggle most consumed product type category
   */
  toggleMostConsumedProductTypeCategory() {
    this.mostConsumedProductTypeCategory = this.mostConsumedProductTypeCategory === 'implant' ? 'body_part' : 'implant';
    this.loadMostConsumedProductType();
  }

  /**
   * Calculate and display date range
   */
  private calculateDateRange(): void {
    console.log('Calculating date range with:', {
      dateFilter: this.dateFilter,
      customFromDate: this.customFromDate,
      customToDate: this.customToDate
    });
    let fromDate: Date;
    let toDate: Date = new Date();

    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 (Sun) - 6 (Sat)
    // Helper to get Monday of current week
    const getMonday = (date: Date) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
      return new Date(d.setDate(diff));
    };
    // Helper to get Sunday of current week
    const getSunday = (date: Date) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + 7; // next Sunday
      return new Date(d.setDate(diff));
    };

    switch (this.dateFilter) {
      case 'today':
        fromDate = new Date();
        toDate = new Date();
        break;
      case 'yesterday':
        fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 1);
        toDate = new Date(fromDate);
        break;
      case 'last_7_days':
        fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 6); // includes today
        toDate = new Date();
        break;
      case 'last_30_days':
        fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 29);
        toDate = new Date();
        break;
      case 'last_90_days':
        fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 89);
        toDate = new Date();
        break;
      case 'this_month':
        fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
        toDate = new Date();
        break;
      case 'last_month':
        fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        toDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'this_year':
        fromDate = new Date(now.getFullYear(), 0, 1);
        toDate = new Date();
        break;
      case 'last_year':
        fromDate = new Date(now.getFullYear() - 1, 0, 1);
        toDate = new Date(now.getFullYear() - 1, 11, 31);
        break;
      case 'lastWeek':
        // Previous full calendar week (Monday to Sunday)
        const lastWeekMonday = getMonday(new Date(now.setDate(now.getDate() - 7)));
        const lastWeekSunday = new Date(lastWeekMonday);
        lastWeekSunday.setDate(lastWeekMonday.getDate() + 6);
        fromDate = lastWeekMonday;
        toDate = lastWeekSunday;
        break;
      case 'this_week':
        // Current week (Monday to today)
        fromDate = getMonday(new Date());
        toDate = new Date();
        break;
      case 'lastThreeWeeks':
        fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 20); // 21 days including today
        toDate = new Date();
        break;
      case 'custom':
        if (this.customFromDate && this.customToDate) {
          fromDate = new Date(this.customFromDate);
          toDate = new Date(this.customToDate);
        } else {
          fromDate = new Date();
          toDate = new Date();
        }
        break;
      default:
        // Try to match patterns like 'last7Days', 'last3Weeks', 'last6Months'
        const lastDaysMatch = this.dateFilter.match(/^last(\d+)Days$/i);
        const lastWeeksMatch = this.dateFilter.match(/^last(\d+)Weeks$/i);
        const lastMonthsMatch = this.dateFilter.match(/^last(\d+)Months$/i);
        if (lastDaysMatch) {
          const days = parseInt(lastDaysMatch[1], 10);
          fromDate = new Date();
          fromDate.setDate(fromDate.getDate() - (days - 1));
          toDate = new Date();
        } else if (lastWeeksMatch) {
          const weeks = parseInt(lastWeeksMatch[1], 10);
          fromDate = new Date();
          fromDate.setDate(fromDate.getDate() - (weeks * 7 - 1));
          toDate = new Date();
        } else if (lastMonthsMatch) {
          const months = parseInt(lastMonthsMatch[1], 10);
          fromDate = new Date();
          fromDate.setMonth(fromDate.getMonth() - months);
          fromDate.setDate(fromDate.getDate() + 1); // To include today
          toDate = new Date();
        } else {
          fromDate = new Date();
          toDate = new Date();
        }
    }

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    this.resolvedDateRange = `${formatDate(fromDate)} - ${formatDate(toDate)}`;
    this.cdr.detectChanges();
  }

  onMetricWidgetClick(widgetLabel: string) {
    // Save current dashboard filter state
    const dashboardState = {
      dateFilter: this.dateFilter,
      customFromDate: this.customFromDate,
      customToDate: this.customToDate,
      storeFilter: this.storeFilter
    };
    sessionStorage.setItem('dashboardV2State', JSON.stringify(dashboardState));

    // Handle Devices navigation separately
    if (widgetLabel === 'Devices') {
      this.router.navigate([`/home/stores/${this.storeFilter}/cabinets`], {
        queryParams: { id: this.storeFilter }
      });
      return;
    }

    // Widget label to route segment mapping for stocks
    const labelToRoute: { [key: string]: string } = {
      'Active': 'active',
      'Consumed': 'consumed',
      'Missing': 'missing',
      'Expired': 'expired',
      'Expiring in 30 Days': 'expiring',
      'Low Stock': 'lowStocks',
      'Transit': 'transit'
    };
    const widgetName = labelToRoute[widgetLabel] || widgetLabel;
    this.router.navigate([`/home/stocks/stores/${this.storeFilter}/implants/${widgetName}`]);
  }
}
